import { inject, injectable } from '@servicetitan/react-ioc';
import { SettingsApi } from 'api';
import { makeObservable, observable, runInAction } from 'mobx';

export type SpecialSettingsDict = Record<string, unknown>;

@injectable()
export class SpecialSettingsStore {
    @observable loaded = false;
    @observable loading = false;
    @observable failed = false;

    private settings: SpecialSettingsDict = {};
    private inflight?: Promise<SpecialSettingsDict>;

    constructor(@inject(SettingsApi) private readonly settingsApi: SettingsApi) {
        makeObservable(this);
    }

    load = (): Promise<SpecialSettingsDict> => {
        if (this.loaded) {
            return Promise.resolve(this.settings);
        }
        if (this.inflight !== undefined) {
            return this.inflight;
        }

        runInAction(() => {
            this.loading = true;
            this.failed = false;
        });

        this.inflight = this.settingsApi.client
            .getAllSpecialSettings()
            .then((result: any) => {
                const dict: SpecialSettingsDict =
                    result && typeof result === 'object' ? result : {};
                runInAction(() => {
                    this.settings = dict;
                    this.loaded = true;
                    this.loading = false;
                });
                return dict;
            })
            .catch(() => {
                runInAction(() => {
                    this.loading = false;
                    this.failed = true;
                });
                return {};
            })
            .finally(() => {
                this.inflight = undefined;
            });

        return this.inflight;
    };

    get<T = unknown>(key: string): T | undefined {
        return this.settings[key] as T | undefined;
    }

    getAsync = async <T = unknown>(key: string): Promise<T | undefined> => {
        await this.load();
        return this.get<T>(key);
    };
}
